// src/components/AgeProofs/AgeProofs.tsx

import React, { useEffect, useState, useCallback } from "react";
import { AgeProof } from "../../../../../types/ageProofs";
import { approveAgeProof, getAgeProofs } from "../../../../../api/adminApi";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Modal,
  Typography,
  TablePagination,
} from "@material-ui/core";
import useSEO from "../../../../../seo/use-seo";
import StandardPageHelmet from "../../../../../seo/components/StandardPageHelmet";

import styles from "./AgeProofsStyles";
import BaseLoading from "../../../../Loader/BaseLoading";
import { showToast } from "../../../../../utils/toast";
import { getFileByIdAPI } from "../../../../../api/userApi";
import videoIconSvg from "../../../../../assets/videoIcon.svg";
import defaultImage from "../../../../../assets/defaultImage.svg";

const AgeProofs = () => {
  const [ageProofs, setAgeProofs] = useState<AgeProof[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [openImage, setOpenImage] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState<string>("idProofSubmittedAt");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const classes = styles();
  const seo = useSEO("age-proofs");

  // Helper function to get Didit verification status information
  const getDiditStatusInfo = (status: string) => {
    const statusInfo = {
      "No Started": {
        label: "Not Started",
        description: "User has not initiated verification",
        color: "#6B7280",
        icon: "🆕",
        adminAction: "No action needed - user needs to start verification",
      },
      "In Progress": {
        label: "In Progress",
        description: "User is currently completing verification",
        color: "#F59E0B",
        icon: "⏳",
        adminAction: "Monitor progress - verification is active",
      },
      Approved: {
        label: "Approved",
        description: "Verification completed and approved",
        color: "#10B981",
        icon: "✅",
        adminAction: "User is verified - can proceed with platform access",
      },
      Declined: {
        label: "Declined",
        description: "Verification was rejected due to issues",
        color: "#EF4444",
        icon: "❌",
        adminAction:
          "User needs to restart verification with correct documents",
      },
      "KYC Expired": {
        label: "KYC Expired",
        description: "Previously verified KYC has expired",
        color: "#F59E0B",
        icon: "⏰",
        adminAction: "User needs to renew their verification",
      },
      "In Review": {
        label: "In Review",
        description: "Verification requires manual review",
        color: "#3B82F6",
        icon: "🔍",
        adminAction: "Admin review required - check documents manually",
      },
      Expired: {
        label: "Expired",
        description: "Verification session timed out",
        color: "#F59E0B",
        icon: "⏰",
        adminAction: "User needs to restart verification process",
      },
      Abandoned: {
        label: "Abandoned",
        description: "User started but did not complete verification",
        color: "#6B7280",
        icon: "🚫",
        adminAction: "User needs to restart verification process",
      },
      unknown: {
        label: "Unknown",
        description: "Verification status is unclear",
        color: "#6B7280",
        icon: "❓",
        adminAction: "Investigate - status needs clarification",
      },
    };

    return (
      statusInfo[status as keyof typeof statusInfo] || {
        label: "Not Proofed",
        description: "No verification data available",
        color: "#6B7280",
        icon: "❌",
        adminAction: "User needs to complete verification",
      }
    );
  };

  const fetchMediaUrl = async (mediaUrl: string) => {
    if (!mediaUrl) return defaultImage;
    
    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    
    const fileId = mediaUrl.split("/api/files/")[1];
    if (!fileId) return defaultImage;

    try {
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching idProof for ${mediaUrl}:`, err);
      // showToast("Failed to load ID proof image", "error");
      return videoIconSvg;
    }
  };

  const fetchAgeProofs = useCallback(
    async (options?: {
      page?: number;
      perPage?: number;
      sortType?: "asc" | "desc";
      sortBy?: string;
    }): Promise<{
      items: AgeProof[];
      totalPages: number;
      currentPage: number;
      totalCount: number;
    }> => {
      try {
        const response = await getAgeProofs(options);
        return response;
      } catch (error) {
        throw error;
      }
    },
    []
  );

  // Load data function
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchAgeProofs({
        page: page + 1, // API uses 1-based page numbers
        perPage: rowsPerPage,
        sortType: sortOrder,
        sortBy: sortBy,
      });

      setAgeProofs(response?.items || []);
      setTotalCount(response?.totalCount || 0);

      // Fetch media URLs for items
      response?.items.forEach(item => {
        if (item.idProof) {
          fetchMediaUrl(item.idProof);
        }
      });
    } catch (error) {
      console.error("Error fetching age proofs:", error);
      showToast("Failed to load age proofs", "error");
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, sortOrder, sortBy, fetchAgeProofs]);

  // Load data when page, rowsPerPage, sortOrder, or sortBy changes
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortOrder, sortBy]);

  if (isLoading) {
    return (
      <>
        <StandardPageHelmet seo={seo} />
        <BaseLoading />
      </>
    );
  }

  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Якщо вже сортуємо по цьому полю, змінюємо напрямок
      setSortOrder(prev => (prev === "desc" ? "asc" : "desc"));
    } else {
      // Якщо нове поле, встановлюємо його і починаємо з asc
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(0); // Скидаємо на першу сторінку при зміні сортування
  };

  const getSortIndicator = (field: string) => {
    if (sortBy !== field) return "";
    return sortOrder === "desc" ? " ↓" : " ↑";
  };

  const handleImageClick = async (rawMediaUrl: string) => {
    let fullSizeUrl = mediaUrls[rawMediaUrl];
    if (!fullSizeUrl) {
      fullSizeUrl = await fetchMediaUrl(rawMediaUrl);
    }
    setImageUrl(fullSizeUrl || defaultImage);
    setOpenImage(true);
  };

  const handleCloseImage = () => {
    setOpenImage(false);
  };

  const handleUserSelection = (userId: string) => {
    setSelectedUsers(prevSelected =>
      prevSelected.includes(userId)
        ? prevSelected.filter(id => id !== userId)
        : [...prevSelected, userId]
    );
  };

  const handleChangeProofStatus = (
    userIds: string[] | string,
    approve: boolean
  ) => {
    const status = approve ? "approved" : "rejected";
    approveAgeProof(userIds, approve)
      .then(() => {
        showToast(`Age proof ${status} successfully!`, "success");
        setAgeProofs(prev =>
          prev.map(proof => {
            if (
              Array.isArray(userIds)
                ? userIds.includes(proof._id)
                : proof._id === userIds
            ) {
              return { ...proof, ageProofed: approve };
            }
            return proof;
          })
        );
      })
      .catch(() => {
        const errStatus = approve ? "approving" : "rejecting";
        console.error(
          `Error ${errStatus} age proof for user with IDs:`,
          userIds
        );
        showToast(`Error ${errStatus} age proof. Please try again.`, "error");
      });
  };

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Box sx={{ position: "relative" }} className={classes.pageContainer}>
        <TableContainer component="div" className={classes.tableContainer}>
          <Table style={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCell}>Select</TableCell>
                <TableCell
                  className={classes.tableCell}
                  onClick={() => handleSort("firstName")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Name{getSortIndicator("firstName")}
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  onClick={() => handleSort("email")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Email{getSortIndicator("email")}
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  onClick={() => handleSort("createdDate")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Account Creation Date{getSortIndicator("createdDate")}
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  onClick={() => handleSort("DOB")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Date of Birth{getSortIndicator("DOB")}
                </TableCell>
                <TableCell className={classes.tableCell}>ID Proof</TableCell>
                <TableCell
                  className={classes.tableCell}
                  onClick={() => handleSort("idProofSubmittedAt")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Date of Submission{getSortIndicator("idProofSubmittedAt")}
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  onClick={() => handleSort("diditVerificationCreatedAt")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Didit Verification Submission Date{getSortIndicator("diditVerificationCreatedAt")}
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  onClick={() => handleSort("ageProofed")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Verification Status{getSortIndicator("ageProofed")}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  Verification Attempts
                </TableCell>
                <TableCell className={classes.tableCell}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {ageProofs.map((user) => (
                <TableRow
                  key={user._id}
                  style={{
                    height: "100px",
                    backgroundColor: selectedUsers.includes(user._id)
                      ? !user.ageProofed
                        ? "#76b85a"
                        : "#b85a7c"
                      : "transparent",
                  }}
                >
                  <TableCell className={classes.tableCell}>
                    <Checkbox
                      style={{ color: "white" }}
                      checked={selectedUsers.includes(user._id)}
                      onChange={() => handleUserSelection(user._id)}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {`${user.firstName} ${user.lastName}`}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {user.email}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {user.accountCreatedAt ||
                    user.createdAt ||
                    user.createdDate
                      ? new Date(
                          user.accountCreatedAt ||
                            user.createdAt ||
                            user.createdDate
                        ).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {user.DOB}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {/* <img
                      src={mediaUrls[user.idProof] || defaultImage}
                      alt="ID Proof"
                      style={{
                        width: "150px",
                        cursor: "pointer",
                        height: "110px",
                      }}
                      onClick={() => handleImageClick(user.idProof)}
                    /> */}

                    {(() => {
                      const status =
                        (user as any)?.diditVerification?.status ||
                        "not_proofed";
                      const statusInfo = getDiditStatusInfo(status);

                      return (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontWeight: "bold",
                              color: statusInfo.color,
                            }}
                          >
                            <span>{statusInfo.icon}</span>
                            <span>{statusInfo.label}</span>
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              lineHeight: "1.3",
                            }}
                          >
                            {statusInfo.description}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#888",
                              fontStyle: "italic",
                              lineHeight: "1.2",
                            }}
                          >
                            Admin: {statusInfo.adminAction}
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {user.idProofSubmittedAt ||
                    (user as any)?.diditVerification?.created_at
                      ? new Date(
                          user.idProofSubmittedAt ||
                            (user as any)?.diditVerification?.created_at
                        ).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {user.dateOfSubmission &&
                    user.dateOfSubmission !== "Not submitted"
                      ? new Date(user.dateOfSubmission).toLocaleString()
                      : user.diditVerification?.createdAt
                      ? new Date(
                          user.diditVerification.createdAt
                        ).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {user.ageProofed ? "Proofed" : "Not Proofed"}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {user.verificationAttempts &&
                    user.verificationAttempts.length > 0 ? (
                      <Box>
                        <Typography
                          variant="body2"
                          style={{
                            fontSize: "12px",
                            color: "#FE5A97",
                            marginBottom: "4px",
                          }}
                        >
                          {user.verificationAttempts.length} attempt
                          {user.verificationAttempts.length !== 1 ? "s" : ""}
                        </Typography>
                        <Box
                          style={{
                            maxHeight: "100px",
                            overflowY: "auto",
                            fontSize: "11px",
                          }}
                        >
                          {user.verificationAttempts.map((attempt, idx) => (
                            <Box
                              key={attempt.id || idx}
                              style={{
                                marginBottom: "6px",
                                padding: "4px 6px",
                                backgroundColor: "#2a2a2a",
                                borderRadius: "4px",
                                borderLeft: `3px solid ${
                                  attempt.status === "Approved"
                                    ? "#10B981"
                                    : attempt.status === "Declined"
                                    ? "#EF4444"
                                    : "#F59E0B"
                                }`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                style={{
                                  display: "block",
                                  fontWeight: "bold",
                                  color: "#fff",
                                }}
                              >
                                {attempt.status || "Unknown"}
                              </Typography>
                              <Typography
                                variant="caption"
                                style={{
                                  display: "block",
                                  color: "#999",
                                  fontSize: "10px",
                                }}
                              >
                                {attempt.createdAt
                                  ? new Date(
                                      attempt.createdAt
                                    ).toLocaleDateString()
                                  : "-"}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : user.diditVerification ? (
                      <Box>
                        <Typography
                          variant="body2"
                          style={{
                            fontSize: "12px",
                            color: "#FE5A97",
                          }}
                        >
                          1 attempt
                        </Typography>
                        <Box
                          style={{
                            marginTop: "4px",
                            padding: "4px 6px",
                            backgroundColor: "#2a2a2a",
                            borderRadius: "4px",
                            borderLeft: `3px solid ${
                              user.diditVerification.status === "Approved"
                                ? "#10B981"
                                : user.diditVerification.status === "Declined"
                                ? "#EF4444"
                                : "#F59E0B"
                            }`,
                          }}
                        >
                          <Typography
                            variant="caption"
                            style={{
                              display: "block",
                              fontWeight: "bold",
                              color: "#fff",
                              fontSize: "11px",
                            }}
                          >
                            {user.diditVerification.status || "Unknown"}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{
                              display: "block",
                              color: "#999",
                              fontSize: "10px",
                            }}
                          >
                            {user.diditVerification.createdAt
                              ? new Date(
                                  user.diditVerification.createdAt
                                ).toLocaleDateString()
                              : "-"}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        style={{ fontSize: "12px", color: "#999" }}
                      >
                        No attempts
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Button
                      onClick={() => handleChangeProofStatus(user._id, true)}
                      style={{ marginRight: "10px", color: "green" }}
                      variant="contained"
                    >
                      ✅
                    </Button>
                    <Button
                      onClick={() => handleChangeProofStatus(user._id, false)}
                      variant="contained"
                      style={{ color: "red" }}
                    >
                      ❌
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, newPage) => {
            setPage(newPage);
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            const newRowsPerPage = parseInt((e.target as HTMLInputElement).value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          classes={{
            root: classes.paginationRoot,
            caption: classes.paginationCaption,
            selectIcon: classes.paginationSelectIcon,
          }}
          SelectProps={{ MenuProps: { classes: { paper: classes.menuPaper } } }}
        />

        {selectedUsers.length > 0 && (
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              display: "flex",
              gap: "10px",
            }}
          >
            <Button
              onClick={() => handleChangeProofStatus(selectedUsers, true)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "white",
                borderRadius: "5px",
              }}
              variant="contained"
            >
              Approve
            </Button>
            <Button
              onClick={() => handleChangeProofStatus(selectedUsers, false)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#f44336",
                color: "white",
                borderRadius: "5px",
              }}
              variant="contained"
            >
              Reject
            </Button>
          </div>
        )}

        {/* Модалка для «зуму» зображення */}
        <Modal
          open={openImage}
          onClose={handleCloseImage}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <img
              src={imageUrl}
              alt="ID Proof"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default AgeProofs;
